import * as THREE from 'three'
import { Component } from './Component'
import { Scene } from './Scene'
import { Renderer } from '../components/Graphics/Renderer'
import { Transform } from '../components/Transform'
import { Engine } from '../core/Engine'

type ComponentCtor<T extends Component> =
  new (gameObject: GameObject) => T
export class GameObject {
  engineRef: WeakRef<Engine>
  get engine(): Engine { return this.engineRef.deref()! }
  scene!: Scene

  private m_parent: GameObject | undefined
  children: GameObject[] = []
  get parent() { return this.m_parent }
  set parent(parent: GameObject | undefined) {
    if (parent === this.m_parent) return
    if (this.m_parent) this.m_parent.children = this.m_parent.children.filter(c => c !== this)
    this.m_parent = parent
    if (parent) parent.children.push(this)
    this.onFamilyChange()
  }


  name: string
  userData: any
  tags: string[] = []
  renderer: Renderer | undefined
  transform: Transform

  isMooving = false

  private components: Component[] = []

  active = true

  constructor(engine: Engine, name = 'GameObject', transform?: { position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3 }) {
    this.name = name
    this.engineRef = new WeakRef(engine) //store engine
    if (transform === undefined) transform = {}

    this.transform = this.addComponent(Transform, transform)
    //console.log('created', this.name)
  }
  hasTag(tag: string) { return this.tags.includes(tag) }
  addTag(tag: string) { if (!this.hasTag(tag)) this.tags.push(tag) }
  removeTag(tag: string) { this.tags = this.tags.filter(t => t !== tag) }
  getAncestorWithTag(tag: string): GameObject | undefined {
    let go: GameObject | undefined = this
    while (go) {
      if (go.hasTag(tag)) return go
      go = go.parent
    }
    return undefined
  }
  addComponent<T extends Component>(ctor: ComponentCtor<T>, data?: any): T {
    const component = new ctor(this)

    if (data) component.setup(data)
    else component.setup({})

    this.components.push(component)
    //store transform and renderer for easy access
    if (component instanceof Renderer) this.renderer = component
    for (const c of this.components) {
      c.onComponentAdded(component)
    }
    return component
  }
  removeComponent(component: Component) {
    component.onDestroy()
    component.setExist(false)
    this.components = this.components.filter(c => c !== component)
  }

  getComponent<T extends Component>(
    type: new (...args: any[]) => T
  ): T | undefined {
    return this.components.find(c => c instanceof type) as T
  }
  getComponents<T extends Component>(
    type: new (...args: any[]) => T
  ): T[] {
    return this.components.filter(c => c instanceof type) as T[]
  }
  start() {
    for (const c of this.components) {
      c.loadStart()
    }
  }
  fixedUpdate(dt: number) {
    if (!this.active) return

    for (const c of this.components) {
      if (c.enabled) c.fixedUpdate(dt)
    }
  }
  update(dt: number) {
    if (!this.active) return
    for (const c of this.components) {
      if (!c.started) {
        c.loadStart()
      }
    }
    for (const c of this.components) {
      if (c.enabled && c.started) c.update(dt)
    }
  }
  onMoveUpdate(): boolean {
    if (this.transform.isDirty()) {
      this.transform.clearDirty()
      for (const c of this.components) {
        c.onMove()
      }
      return true
    }
    return false
  }
  onAfterMoveUpdate() {
    for (const c of this.components) {
      c.onAfterMove()
    }
  }
  destroy() {
    //console.log('destroying', this.name)
    this.scene.destroyGameObject(this)
  }
  onDestroy() {
    for (const c of this.components) {
      c.onDestroy()
    }
    this.parent = undefined
  }
  onFamilyChange() { }
  render(_dt: number) {
    for (const c of this.components) {
      c.render(_dt)
    }
  }
}
